<template>
  <header class="navbar" role="banner">
    <div class="container navbar-inner">
      <RouterLink to="/" class="brand" aria-label="الرئيسية — عقارات الكويت">
        <AppLogo />
      </RouterLink>

      <nav
        id="main-nav"
        class="nav"
        :class="{ open: menuOpen }"
        aria-label="التنقل الرئيسي"
      >
        <RouterLink
          v-for="link in links"
          :key="link.to"
          :to="link.to"
          class="nav-link"
          active-class="active"
          @click="menuOpen = false"
        >
          <svg
            class="nav-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
            v-html="link.icon"
          />
          {{ link.label }}
        </RouterLink>
      </nav>

      <div class="navbar-tools">
        <ThemeToggle />
        <RouterLink to="/properties" class="btn btn-primary btn-sm nav-cta">
          تصفح العقارات
        </RouterLink>
        <button
          class="menu-toggle"
          :aria-expanded="menuOpen"
          aria-controls="main-nav"
          aria-label="القائمة"
          @click="menuOpen = !menuOpen"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path
              v-if="!menuOpen"
              fill="currentColor"
              d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"
            />
            <path
              v-else
              fill="currentColor"
              d="M6.4 5L5 6.4l5.6 5.6L5 17.6 6.4 19l5.6-5.6L17.6 19 19 17.6 13.4 12 19 6.4 17.6 5 12 10.6 6.4 5z"
            />
          </svg>
        </button>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref } from "vue";
import { RouterLink } from "vue-router";
import AppLogo from "@/components/mkdf/AppLogo.vue";
import ThemeToggle from "@/components/layout/ThemeToggle.vue";

const menuOpen = ref(false);

const links = [
  {
    to: "/",
    label: "الرئيسية",
    icon: '<path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1h-5v-6H9v6H4a1 1 0 01-1-1V9.5z"/>',
  },
  {
    to: "/properties",
    label: "العقارات",
    icon: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
  },
  {
    to: "/areas",
    label: "المناطق",
    icon: '<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>',
  },
  {
    to: "/services",
    label: "خدماتنا",
    icon: '<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z"/>',
  },
  {
    to: "/blog",
    label: "المدونة",
    icon: '<path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6z"/>',
  },
  {
    to: "/contact",
    label: "تواصل",
    icon: '<path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>',
  },
];
</script>

<style scoped>
.navbar {
  position: sticky;
  top: 0;
  z-index: 100;
  height: var(--navbar-height);
  background: var(--surface-navbar);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border);
}

.navbar-inner {
  display: flex;
  align-items: center;
  height: 100%;
  gap: 24px;
}

.brand {
  flex-shrink: 0;
  text-decoration: none;
}

.brand:hover :deep(.app-logo__text) {
  color: var(--brand);
}

.nav {
  display: none;
  align-items: center;
  gap: 4px;
  flex: 1;
}

.nav-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  font-size: var(--text-base);
  font-weight: 500;
  color: var(--content-body);
  border-radius: var(--radius-md);
  text-decoration: none;
  transition: background var(--motion), color var(--motion);
}

.nav-link:hover {
  background: var(--brand-muted);
  color: var(--brand);
}

.nav-link.active {
  background: var(--brand-muted);
  color: var(--brand);
}

.nav-icon {
  width: 20px;
  height: 20px;
}

.navbar-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-inline-start: auto;
}

.nav-cta {
  display: none;
}

@media (min-width: 1024px) {
  .nav-cta {
    display: inline-flex;
  }
}

.btn-sm {
  padding: 8px 16px;
  font-size: 0.875rem;
}

.menu-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  color: var(--content-body);
  cursor: pointer;
}

.menu-toggle:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 2px;
}

@media (min-width: 1024px) {
  .nav {
    display: flex;
  }

  .menu-toggle {
    display: none;
  }
}

@media (max-width: 1023px) {
  .nav {
    position: absolute;
    inset-inline: 0;
    top: var(--navbar-height);
    flex-direction: column;
    align-items: stretch;
    background: var(--surface-card);
    box-shadow: var(--shadow-lg);
    padding: 12px 16px;
    border-bottom: 1px solid var(--border);
  }

  .nav.open {
    display: flex;
  }

  .nav-link {
    padding: 12px 16px;
  }
}
</style>
